import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { AdminPageHeader, Card, Select, Textarea, Btn, Spinner, ErrorBanner, Tag, EmptyState } from "./_shared";
import {
  Sparkles, Copy, Check, Download, RefreshCw, Image as ImageIcon, Layers,
  CalendarDays, MessageSquareQuote, FileText, Wand2, Stamp,
} from "lucide-react";

// ────────────────────────────────────────────────────────────────────────────────
// Top-level tabs
// ────────────────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "visuals",   label: "Visuals",        icon: ImageIcon },
  { id: "daily",     label: "Daily Summary",  icon: CalendarDays },
  { id: "carousels", label: "Carousels",      icon: Layers },
  { id: "poster",    label: "Branded Poster", icon: Stamp },
  { id: "custom",    label: "Custom AI",      icon: Wand2 },
  { id: "drafts",    label: "Text Drafts",    icon: FileText },
];

export const ContentStudio = () => {
  const [tab, setTab] = useState("visuals");
  return (
    <div className="space-y-5" data-testid="content-studio-page">
      <AdminPageHeader
        eyebrow="CONTENT STUDIO"
        title="Ready-to-share civic content"
        sub="Auto-generate branded social cards, carousels and AI captions from real citizen messages. Review before posting — never invents data."
      />

      <div className="flex flex-wrap gap-2 border-b-2 border-[#1f1f1f] pb-2" data-testid="cs-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`cs-tab-${t.id}`}
              style={{
                background: active ? "#FF9933" : "transparent",
                color: active ? "#0A0A0A" : "#FDFBF7",
                borderColor: active ? "#FF9933" : "#1f1f1f",
              }}
              className="border-2 px-3 py-2 text-[10px] uppercase font-extrabold tracking-[0.25em] inline-flex items-center gap-2 hover:opacity-90 transition-opacity"
            >
              <Icon size={12} strokeWidth={3} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "visuals"   && <VisualsTab />}
      {tab === "daily"     && <DailySummaryTab />}
      {tab === "carousels" && <CarouselsTab />}
      {tab === "poster"    && <PosterTab />}
      {tab === "custom"    && <CustomTab />}
      {tab === "drafts"    && <DraftsTab />}
    </div>
  );
};

export default ContentStudio;

// ────────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ────────────────────────────────────────────────────────────────────────────────

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

const renderToBlob = async (payload) => {
  const r = await adminApi.post("/admin/content-studio/render", payload, {
    responseType: "blob",
  });
  return r.data;
};

const CopyButton = ({ text, testId, label = "Copy" }) => {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <Btn
      variant="ghost"
      data-testid={testId}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch { /* ignore */ }
      }}
    >
      {copied ? <><Check size={12} strokeWidth={3} /> Copied</> : <><Copy size={12} strokeWidth={3} /> {label}</>}
    </Btn>
  );
};

const CaptionBlock = ({ label, text, testIdBase }) => (
  <div className="border-2 border-[#1f1f1f] p-3" data-testid={`${testIdBase}-block`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-[10px] uppercase font-extrabold tracking-[0.3em] text-neutral-400">
        {label}
      </span>
      <CopyButton text={text} testId={`${testIdBase}-copy`} />
    </div>
    <pre
      className="whitespace-pre-wrap text-sm leading-relaxed font-sans m-0"
      style={{ color: "#FDFBF7" }}
      data-testid={`${testIdBase}-text`}
    >
      {text}
    </pre>
  </div>
);

// ────────────────────────────────────────────────────────────────────────────────
// VISUALS tab — trending / story cards from a citizen message + AI caption
// ────────────────────────────────────────────────────────────────────────────────

const VisualsTab = () => {
  const [candidates, setCandidates] = useState([]);
  const [loadingCands, setLoadingCands] = useState(true);
  const [err, setErr] = useState(null);

  const [selectedId, setSelectedId] = useState("");
  const [kind, setKind] = useState("trending"); // 'trending' | 'story'
  const [hashtags, setHashtags] = useState(true);

  const [imgUrl, setImgUrl] = useState("");
  const [imgBlob, setImgBlob] = useState(null);
  const [generating, setGenerating] = useState(false);

  const [captions, setCaptions] = useState(null);
  const [captionLoading, setCaptionLoading] = useState(false);
  const [captionErr, setCaptionErr] = useState(null);

  const selected = useMemo(
    () => candidates.find((c) => c.id === selectedId) || null,
    [candidates, selectedId]
  );

  const loadCandidates = async () => {
    setLoadingCands(true); setErr(null);
    try {
      const r = await adminApi.get("/admin/content-studio/candidates?limit=20");
      const list = r.data?.candidates || [];
      setCandidates(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setErr(
          "Content Studio endpoints aren't on the current deployment yet. Redeploy production from preview to enable this tab."
        );
      } else {
        setErr(e?.response?.data?.detail || "Could not load candidate messages.");
      }
    } finally {
      setLoadingCands(false);
    }
  };

  useEffect(() => { loadCandidates(); /* eslint-disable-next-line */ }, []);

  // Reset image when selection or kind changes
  useEffect(() => {
    if (imgUrl) URL.revokeObjectURL(imgUrl);
    setImgUrl(""); setImgBlob(null); setCaptions(null);
    // eslint-disable-next-line
  }, [selectedId, kind]);

  const generate = async () => {
    if (!selectedId) return;
    setGenerating(true); setErr(null);
    setCaptions(null); setCaptionErr(null);
    try {
      const blob = await renderToBlob({ kind, message_id: selectedId });
      const url = URL.createObjectURL(blob);
      setImgBlob(blob);
      setImgUrl(url);

      // Fire caption generation in parallel — non-blocking for the image preview
      setCaptionLoading(true);
      adminApi
        .post("/admin/content-studio/caption", {
          kind, message_id: selectedId, format: "all", hashtags,
        })
        .then((r) => setCaptions(r.data?.captions || {}))
        .catch((e) => setCaptionErr(e?.response?.data?.detail || "Caption generation failed."))
        .finally(() => setCaptionLoading(false));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Image render failed.");
    } finally {
      setGenerating(false);
    }
  };

  const download = () => {
    if (!imgBlob) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
    downloadBlob(imgBlob, `kerala-speaks-${kind}-${stamp}.png`);
  };

  return (
    <div className="space-y-5">
      <Card title="Pick a message">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <p className="text-xs text-neutral-400">
            Showing top {candidates.length} approved messages. Flagged / spam / removed posts are excluded automatically.
          </p>
          <Btn variant="ghost" onClick={loadCandidates} data-testid="cs-vis-refresh">
            <RefreshCw size={12} strokeWidth={3} /> Refresh
          </Btn>
        </div>

        {err && <ErrorBanner message={err} />}
        {loadingCands && <Spinner label="Loading top messages…" />}
        {!loadingCands && !err && !candidates.length && (
          <EmptyState title="No approved messages yet" sub="Approve at least one message in Moderation to start generating visuals." />
        )}

        {!loadingCands && candidates.length > 0 && (
          <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1" data-testid="cs-vis-candidates">
            {candidates.map((c) => {
              const active = c.id === selectedId;
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  data-testid={`cs-vis-candidate-${c.id}`}
                  className="text-left w-full border-2 p-3 transition-colors hover:opacity-95"
                  style={{
                    borderColor: active ? "#FF9933" : "#1f1f1f",
                    background: active ? "rgba(255,153,51,0.08)" : "transparent",
                  }}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Tag color="#FF9933">{c.upvotes} upvotes</Tag>
                    {c.category && <Tag color="#FDFBF7">{c.category}</Tag>}
                    {c.district && <Tag color="#138808">{c.district}</Tag>}
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: "#FDFBF7" }}>
                    {c.message?.length > 200 ? `${c.message.slice(0, 200)}…` : c.message}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <Card title="Generate visual + caption">
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <Field label="Format">
            <Select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="cs-vis-kind">
              <option value="trending">Trending — 1200 × 1200 (Instagram, X)</option>
              <option value="story">Story — 1080 × 1920 (IG/WA Status)</option>
            </Select>
          </Field>
          <Field label="Hashtags">
            <Select value={hashtags ? "yes" : "no"} onChange={(e) => setHashtags(e.target.value === "yes")} data-testid="cs-vis-hashtags">
              <option value="yes">Include hashtags</option>
              <option value="no">No hashtags</option>
            </Select>
          </Field>
          <div className="flex items-end">
            <Btn onClick={generate} disabled={!selectedId || generating} data-testid="cs-vis-generate">
              <Sparkles size={14} strokeWidth={3} />
              {generating ? "Rendering…" : "Generate"}
            </Btn>
          </div>
        </div>

        {selected && (
          <p className="text-xs text-neutral-400 mb-3">
            Source: <span style={{ color: "#FDFBF7" }}>{selected.message?.slice(0, 120)}{selected.message?.length > 120 ? "…" : ""}</span>
          </p>
        )}
      </Card>

      {(imgUrl || generating) && (
        <div className="space-y-5">
          <Card
            title="Preview"
            action={imgBlob && (
              <Btn variant="ghost" onClick={download} data-testid="cs-vis-download">
                <Download size={12} strokeWidth={3} /> Download PNG
              </Btn>
            )}
          >
            {generating && <Spinner label="Rendering image…" />}
            {!generating && imgUrl && (
              <div className="flex items-center justify-center p-4 border-2 border-[#1f1f1f] bg-[#0A0A0A]">
                <img
                  src={imgUrl}
                  alt="Generated card"
                  data-testid="cs-vis-preview"
                  style={{ maxWidth: "min(720px, 100%)", width: "100%", height: "auto", display: "block" }}
                />
              </div>
            )}
          </Card>

          <Card title="AI caption — copy and post">
            {captionLoading && <Spinner label="Claude is writing captions…" />}
            {captionErr && <ErrorBanner message={captionErr} />}
            {!captionLoading && !captionErr && !captions && (
              <p className="text-sm text-neutral-500">Captions will appear here once the image renders.</p>
            )}
            {captions && (
              <div className="grid md:grid-cols-2 gap-3" data-testid="cs-vis-captions">
                {captions.INSTAGRAM_EN && (
                  <CaptionBlock label="Instagram · English" text={captions.INSTAGRAM_EN} testIdBase="cs-vis-cap-ig-en" />
                )}
                {captions.INSTAGRAM_ML && (
                  <CaptionBlock label="Instagram · Malayalam" text={captions.INSTAGRAM_ML} testIdBase="cs-vis-cap-ig-ml" />
                )}
                {captions.TWITTER_EN && (
                  <CaptionBlock label="X / Twitter · English" text={captions.TWITTER_EN} testIdBase="cs-vis-cap-x-en" />
                )}
                {captions.TWITTER_ML && (
                  <CaptionBlock label="X / Twitter · Malayalam" text={captions.TWITTER_ML} testIdBase="cs-vis-cap-x-ml" />
                )}
                {captions.WHATSAPP_EN && (
                  <CaptionBlock label="WhatsApp · English" text={captions.WHATSAPP_EN} testIdBase="cs-vis-cap-wa-en" />
                )}
                {captions.WHATSAPP_ML && (
                  <CaptionBlock label="WhatsApp · Malayalam" text={captions.WHATSAPP_ML} testIdBase="cs-vis-cap-wa-ml" />
                )}
                {captions.HASHTAGS && (
                  <div className="md:col-span-2">
                    <CaptionBlock label="Hashtags" text={captions.HASHTAGS} testIdBase="cs-vis-cap-tags" />
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────────
// DAILY SUMMARY tab — one-click "today's civic snapshot"
// ────────────────────────────────────────────────────────────────────────────────

const DailySummaryTab = () => {
  const [imgUrl, setImgUrl] = useState("");
  const [imgBlob, setImgBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [captions, setCaptions] = useState(null);
  const [captionLoading, setCaptionLoading] = useState(false);
  const [captionErr, setCaptionErr] = useState(null);

  const [dateLabel, setDateLabel] = useState("TODAY KERALA SPOKE");
  const [hashtags, setHashtags] = useState(true);

  const generate = async () => {
    setLoading(true); setErr(null); setCaptions(null); setCaptionErr(null);
    if (imgUrl) URL.revokeObjectURL(imgUrl);
    try {
      const blob = await renderToBlob({ kind: "daily", date_label: dateLabel || "TODAY KERALA SPOKE" });
      const url = URL.createObjectURL(blob);
      setImgBlob(blob);
      setImgUrl(url);

      setCaptionLoading(true);
      adminApi
        .post("/admin/content-studio/caption", { kind: "daily", format: "all", hashtags })
        .then((r) => setCaptions(r.data?.captions || {}))
        .catch((e) => setCaptionErr(e?.response?.data?.detail || "Caption generation failed."))
        .finally(() => setCaptionLoading(false));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Daily summary render failed.");
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    if (!imgBlob) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 10);
    downloadBlob(imgBlob, `kerala-speaks-daily-${stamp}.png`);
  };

  return (
    <div className="space-y-5">
      <Card title="Today's civic snapshot">
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <Field label="Headline label">
            <input
              className="border-2 border-[#1f1f1f] bg-[#0A0A0A] px-3 py-2 text-sm w-full"
              style={{ color: "#FDFBF7" }}
              value={dateLabel}
              onChange={(e) => setDateLabel(e.target.value)}
              maxLength={48}
              data-testid="cs-daily-label"
            />
          </Field>
          <Field label="Hashtags">
            <Select value={hashtags ? "yes" : "no"} onChange={(e) => setHashtags(e.target.value === "yes")} data-testid="cs-daily-hashtags">
              <option value="yes">Include hashtags</option>
              <option value="no">No hashtags</option>
            </Select>
          </Field>
          <div className="flex items-end">
            <Btn onClick={generate} disabled={loading} data-testid="cs-daily-generate">
              <Sparkles size={14} strokeWidth={3} /> {loading ? "Rendering…" : "Generate daily card"}
            </Btn>
          </div>
        </div>
        <p className="text-xs text-neutral-500">
          Auto-uses today's top 3 categories from the last 24 hours, plus running totals. No data is invented.
        </p>
        {err && <div className="mt-3"><ErrorBanner message={err} /></div>}
      </Card>

      {(imgUrl || loading) && (
        <div className="space-y-5">
          <Card
            title="Preview"
            action={imgBlob && (
              <Btn variant="ghost" onClick={download} data-testid="cs-daily-download">
                <Download size={12} strokeWidth={3} /> Download PNG
              </Btn>
            )}
          >
            {loading && <Spinner label="Rendering daily card…" />}
            {!loading && imgUrl && (
              <div className="flex items-center justify-center p-4 border-2 border-[#1f1f1f] bg-[#0A0A0A]">
                <img src={imgUrl} alt="Daily summary" data-testid="cs-daily-preview"
                     style={{ maxWidth: "min(720px, 100%)", width: "100%", height: "auto" }} />
              </div>
            )}
          </Card>

          <Card title="AI caption">
            {captionLoading && <Spinner label="Claude is writing captions…" />}
            {captionErr && <ErrorBanner message={captionErr} />}
            {captions && (
              <div className="grid md:grid-cols-2 gap-3" data-testid="cs-daily-captions">
                {captions.INSTAGRAM_EN && <CaptionBlock label="Instagram · English" text={captions.INSTAGRAM_EN} testIdBase="cs-daily-cap-ig-en" />}
                {captions.INSTAGRAM_ML && <CaptionBlock label="Instagram · Malayalam" text={captions.INSTAGRAM_ML} testIdBase="cs-daily-cap-ig-ml" />}
                {captions.TWITTER_EN && <CaptionBlock label="X · English" text={captions.TWITTER_EN} testIdBase="cs-daily-cap-x-en" />}
                {captions.TWITTER_ML && <CaptionBlock label="X · Malayalam" text={captions.TWITTER_ML} testIdBase="cs-daily-cap-x-ml" />}
                {captions.WHATSAPP_EN && <CaptionBlock label="WhatsApp · English" text={captions.WHATSAPP_EN} testIdBase="cs-daily-cap-wa-en" />}
                {captions.WHATSAPP_ML && <CaptionBlock label="WhatsApp · Malayalam" text={captions.WHATSAPP_ML} testIdBase="cs-daily-cap-wa-ml" />}
                {captions.HASHTAGS && (
                  <div className="md:col-span-2">
                    <CaptionBlock label="Hashtags" text={captions.HASHTAGS} testIdBase="cs-daily-cap-tags" />
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────────
// CAROUSELS tab — auto-plan + render N-slide carousel
// ────────────────────────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────────────────────
// Shared poll picker — fetches all platform campaigns once, lets the user pick
// one, and surfaces the selected poll's metadata to the parent tab.
// ────────────────────────────────────────────────────────────────────────────────

const usePolls = () => {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get("/admin/content-studio/polls");
        if (!cancelled) setPolls(r.data?.polls || []);
      } catch {
        // ignore — picker just won't have entries; tab still works as "Custom"
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return { polls, loading };
};

const CampaignPickerCard = ({ polls, loading, value, onSelect, testIdBase }) => {
  const selected = polls.find((p) => p.id === value);
  return (
    <Card title="Campaign context">
      <p className="text-xs text-neutral-400 mb-3">
        Pick a poll to anchor this post in. Cover + caption tone will reflect the campaign.
        Leave on "Custom" to write from scratch.
      </p>
      <Select
        value={value || ""}
        onChange={(e) => onSelect(e.target.value || "")}
        disabled={loading}
        data-testid={`${testIdBase}-poll`}
      >
        <option value="">— Custom (no campaign anchor) —</option>
        {polls.map((p) => (
          <option key={p.id} value={p.id}>
            {p.title} · {Object.entries(p.stats || {}).map(([k, v]) => `${v} ${k}`).join(" · ")} · {p.status}
          </option>
        ))}
      </Select>
      {selected && (
        <div className="mt-3 border-2 border-[#1f1f1f] px-3 py-2" data-testid={`${testIdBase}-poll-preview`}>
          <p className="text-[10px] uppercase font-extrabold tracking-[0.3em]" style={{ color: "#FF9933" }}>
            {selected.suggested_headline || selected.title}
          </p>
          <p className="text-xs text-neutral-400 mt-1">{selected.suggested_sub || selected.headline}</p>
          <p className="text-[10px] uppercase font-bold tracking-[0.25em] text-neutral-500 mt-2">
            Suggested tone: {selected.suggested_tone || "curious"}
          </p>
        </div>
      )}
    </Card>
  );
};



const CarouselsTab = () => {
  const { polls, loading: pollsLoading } = usePolls();
  const [pollId, setPollId] = useState("");
  const selectedPoll = polls.find((p) => p.id === pollId) || null;

  const [slides, setSlides] = useState(5);
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [err, setErr] = useState(null);

  // map slideIndex → { url, blob }
  const [renders, setRenders] = useState({});
  const [renderingIdx, setRenderingIdx] = useState(null);
  const [renderingAll, setRenderingAll] = useState(false);

  useEffect(() => () => {
    // cleanup blobs on unmount
    Object.values(renders).forEach((r) => r?.url && URL.revokeObjectURL(r.url));
    // eslint-disable-next-line
  }, []);

  const buildPlan = async () => {
    setLoadingPlan(true); setErr(null); setRenders({}); setPlan(null);
    try {
      const r = await adminApi.post("/admin/content-studio/carousel-plan", { slides });
      setPlan(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Plan failed.");
    } finally {
      setLoadingPlan(false);
    }
  };

  const renderSlide = async (idx) => {
    if (!plan?.slides?.[idx]) return;
    const slide = plan.slides[idx];
    setRenderingIdx(idx);
    try {
      const payload = { kind: slide.kind };
      if (slide.message_id) payload.message_id = slide.message_id;
      // Inject campaign metadata into the cover slide so the carousel opens
      // with the campaign-specific headline + sub instead of the generic default.
      if (slide.kind === "cover" && selectedPoll) {
        payload.headline = selectedPoll.suggested_headline || selectedPoll.title;
        payload.sub = selectedPoll.suggested_sub || selectedPoll.headline;
      }
      const blob = await renderToBlob(payload);
      const url = URL.createObjectURL(blob);
      setRenders((prev) => {
        if (prev[idx]?.url) URL.revokeObjectURL(prev[idx].url);
        return { ...prev, [idx]: { url, blob } };
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || `Slide ${idx + 1} render failed.`);
    } finally {
      setRenderingIdx(null);
    }
  };

  const renderAll = async () => {
    if (!plan?.slides?.length) return;
    setRenderingAll(true);
    for (let i = 0; i < plan.slides.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await renderSlide(i);
    }
    setRenderingAll(false);
  };

  const downloadSlide = (idx) => {
    const r = renders[idx];
    if (!r?.blob) return;
    downloadBlob(r.blob, `kerala-speaks-carousel-${String(idx + 1).padStart(2, "0")}.png`);
  };

  const downloadAll = () => {
    Object.entries(renders).forEach(([idx, r]) => {
      if (r?.blob) downloadBlob(r.blob, `kerala-speaks-carousel-${String(Number(idx) + 1).padStart(2, "0")}.png`);
    });
  };

  return (
    <div className="space-y-5">
      <CampaignPickerCard
        polls={polls} loading={pollsLoading}
        value={pollId} onSelect={setPollId}
        testIdBase="cs-car"
      />
      <Card title="Carousel plan">
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <Field label="Slides (3 – 6)">
            <Select value={String(slides)} onChange={(e) => setSlides(Number(e.target.value))} data-testid="cs-car-slides">
              {[3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} slides</option>)}
            </Select>
          </Field>
          <div className="flex items-end gap-2">
            <Btn onClick={buildPlan} disabled={loadingPlan} data-testid="cs-car-plan">
              <Layers size={14} strokeWidth={3} /> {loadingPlan ? "Planning…" : "Plan carousel"}
            </Btn>
          </div>
          <div className="flex items-end gap-2">
            <Btn onClick={renderAll} disabled={!plan?.slides?.length || renderingAll} data-testid="cs-car-render-all">
              <Sparkles size={14} strokeWidth={3} /> {renderingAll ? "Rendering…" : "Render all slides"}
            </Btn>
          </div>
        </div>
        <p className="text-xs text-neutral-500">
          Auto-builds: Cover → top {Math.max(1, slides - 2)} citizen messages → call-to-action.
        </p>
        {err && <div className="mt-3"><ErrorBanner message={err} /></div>}
      </Card>

      {plan?.slides?.length > 0 && (
        <Card
          title={`Slides (${plan.total_slides})`}
          action={Object.keys(renders).length > 0 && (
            <Btn variant="ghost" onClick={downloadAll} data-testid="cs-car-download-all">
              <Download size={12} strokeWidth={3} /> Download all
            </Btn>
          )}
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cs-car-grid">
            {plan.slides.map((s, idx) => {
              const r = renders[idx];
              return (
                <div key={idx} className="border-2 border-[#1f1f1f] p-3 flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-2">
                    <Tag color="#FF9933">{idx + 1} / {plan.total_slides}</Tag>
                    <Tag color="#FDFBF7">{s.kind}</Tag>
                  </div>
                  <p className="text-xs uppercase font-extrabold tracking-[0.25em] text-neutral-300">
                    {s.label}
                  </p>
                  {s.preview && (
                    <p className="text-xs text-neutral-400 line-clamp-3">{s.preview}</p>
                  )}
                  <div className="aspect-square w-full border-2 border-[#1f1f1f] bg-[#0A0A0A] flex items-center justify-center overflow-hidden">
                    {renderingIdx === idx && <Spinner label="Rendering…" />}
                    {!renderingIdx && r?.url && (
                      <img src={r.url} alt={`Slide ${idx + 1}`} data-testid={`cs-car-slide-${idx}`}
                           style={{ maxWidth: "100%", maxHeight: "100%" }} />
                    )}
                    {!r?.url && renderingIdx !== idx && (
                      <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-600">
                        Not rendered
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Btn variant="ghost" onClick={() => renderSlide(idx)} disabled={renderingIdx === idx} data-testid={`cs-car-render-${idx}`}>
                      <RefreshCw size={12} strokeWidth={3} /> Render
                    </Btn>
                    {r?.blob && (
                      <Btn variant="ghost" onClick={() => downloadSlide(idx)} data-testid={`cs-car-download-${idx}`}>
                        <Download size={12} strokeWidth={3} /> PNG
                      </Btn>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};


// ────────────────────────────────────────────────────────────────────────────────
// BRANDED POSTER tab — Pillow-based campaign card. Deterministic, free, on-brand.
// ────────────────────────────────────────────────────────────────────────────────

const POSTER_ASPECTS = [
  { v: "poster_square", label: "Square (1200×1200) — Instagram feed",  box: "aspect-square" },
  { v: "poster_story",  label: "Story (1080×1920) — IG/WhatsApp Story", box: "aspect-[9/16]" },
];

const PosterTab = () => {
  const { polls, loading: pollsLoading } = usePolls();
  const [selectedPollId, setSelectedPollId] = useState("");

  const [headline, setHeadline] = useState("Kerala has spoken.");
  const [sub, setSub] = useState("Kerala's first citizen-led, anonymous Chief Minister poll. Before the campaigns. Before the noise. Just citizens.");
  const [kind, setKind] = useState("poster_square");
  const [showTotals, setShowTotals] = useState(true);

  const [imgUrl, setImgUrl] = useState("");
  const [imgBlob, setImgBlob] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState(null);

  const boxClass = POSTER_ASPECTS.find((a) => a.v === kind)?.box || "aspect-square";

  // When a poll is picked, auto-fill headline + sub from the campaign metadata
  const onSelectPoll = (id) => {
    setSelectedPollId(id);
    if (!id) return;
    const p = polls.find((x) => x.id === id);
    if (!p) return;
    setHeadline(p.suggested_headline || p.title || "");
    setSub(p.suggested_sub || p.headline || "");
  };

  useEffect(() => () => {
    if (imgUrl) URL.revokeObjectURL(imgUrl);
    // eslint-disable-next-line
  }, []);

  const generate = async () => {
    setGenerating(true); setErr(null);
    if (imgUrl) URL.revokeObjectURL(imgUrl);
    setImgUrl(""); setImgBlob(null);
    try {
      const r = await adminApi.post(
        "/admin/content-studio/render",
        {
          kind,
          headline: headline.trim() || "Kerala Speaks",
          sub: sub.trim() || "",
          show_totals: showTotals,
        },
        { responseType: "blob" }
      );
      const blob = r.data;
      const url = URL.createObjectURL(blob);
      setImgBlob(blob);
      setImgUrl(url);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setErr("Branded poster endpoint isn't deployed yet. Redeploy production from preview to enable this.");
      } else {
        setErr(e?.response?.data?.detail || "Poster render failed.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const download = () => {
    if (!imgBlob) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
    downloadBlob(imgBlob, `kerala-speaks-poster-${kind}-${stamp}.png`);
  };

  return (
    <div className="space-y-5">
      <Card title="Campaign poster">
        <p className="text-xs text-neutral-400 mb-3">
          Uses the same branded Pillow engine as your home-page share images.
          Deterministic, instant, free — no LLM cost.
        </p>

        <Field label="Pick a campaign (auto-fills headline + sub)">
          <Select
            value={selectedPollId}
            onChange={(e) => onSelectPoll(e.target.value)}
            disabled={pollsLoading}
            data-testid="cs-poster-poll"
          >
            <option value="">— Custom (write your own headline) —</option>
            {polls.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title} · {Object.entries(p.stats || {}).map(([k, v]) => `${v} ${k}`).join(" · ")} · {p.status}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <Field label="Headline (large)">
            <input
              type="text"
              maxLength={80}
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              className="border-2 border-[#1f1f1f] bg-[#0A0A0A] px-3 py-2 text-sm w-full"
              style={{ color: "#FDFBF7" }}
              data-testid="cs-poster-headline"
            />
          </Field>
          <Field label="Aspect">
            <Select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="cs-poster-kind">
              {POSTER_ASPECTS.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
            </Select>
          </Field>
        </div>

        <Field label="Sub-headline">
          <Textarea
            rows={3}
            maxLength={200}
            value={sub}
            onChange={(e) => setSub(e.target.value)}
            data-testid="cs-poster-sub"
          />
        </Field>

        <div className="mt-4">
          <Field label="Live totals on the image">
            <Select value={showTotals ? "yes" : "no"} onChange={(e) => setShowTotals(e.target.value === "yes")} data-testid="cs-poster-totals">
              <option value="yes">Show live messages + upvotes</option>
              <option value="no">No stats — headline only</option>
            </Select>
          </Field>
        </div>

        <div className="mt-5">
          <Btn onClick={generate} disabled={generating || !headline.trim()} data-testid="cs-poster-generate">
            <Stamp size={14} strokeWidth={3} /> {generating ? "Rendering…" : "Generate poster"}
          </Btn>
        </div>

        {err && <div className="mt-3"><ErrorBanner message={err} /></div>}
      </Card>

      {(imgUrl || generating) && (
        <Card
          title="Preview"
          action={imgBlob && (
            <Btn variant="ghost" onClick={download} data-testid="cs-poster-download">
              <Download size={12} strokeWidth={3} /> Download PNG
            </Btn>
          )}
        >
          {generating && <Spinner label="Rendering branded poster…" />}
          {!generating && imgUrl && (
            <div className={`mx-auto ${boxClass} border-2 border-[#1f1f1f] bg-[#0A0A0A] overflow-hidden flex items-center justify-center`}
                 style={{ maxWidth: kind === "poster_story" ? "420px" : "640px", width: "100%" }}>
              <img src={imgUrl} alt="Branded poster"
                   data-testid="cs-poster-preview"
                   style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          )}
        </Card>
      )}
    </div>
  );
};


// ────────────────────────────────────────────────────────────────────────────────
// CUSTOM tab — prompt-driven AI image generation (Gemini Nano Banana)
// ────────────────────────────────────────────────────────────────────────────────

const ASPECTS = [
  { v: "square",    label: "Square (1:1) — Instagram feed", box: "aspect-square" },
  { v: "portrait",  label: "Portrait (9:16) — Story / Reel", box: "aspect-[9/16]" },
  { v: "landscape", label: "Landscape (16:9) — X / Facebook", box: "aspect-video" },
];
const STYLES = [
  { v: "",             label: "No preset — follow prompt verbatim" },
  { v: "editorial",    label: "Editorial — magazine cover" },
  { v: "photo",        label: "Photorealistic photo" },
  { v: "illustration", label: "Vector illustration" },
  { v: "poster",       label: "Civic poster — bold flat shapes" },
];

const CustomTab = () => {
  const { polls, loading: pollsLoading } = usePolls();
  const [pollId, setPollId] = useState("");
  const selectedPoll = polls.find((p) => p.id === pollId) || null;

  const [prompt, setPrompt] = useState(
    "A vibrant Kerala-themed civic poster celebrating citizens choosing their next Chief Minister, saffron and green accents, sunrise over backwaters"
  );
  const [count, setCount] = useState(1);
  const [aspect, setAspect] = useState("square");
  const [style, setStyle] = useState("poster");
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState(null);
  // history: [{ id, mime_type, data_base64, prompt, aspect_ratio, style, created_at, url, branded_url?, branded_blob?, branding? }]
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  // captionState: { [imageId]: { loading, captions, err } }
  const [captionState, setCaptionState] = useState({});

  // Cleanup blob URLs (branded versions) on unmount
  useEffect(() => () => {
    history.forEach((im) => {
      if (im.branded_url?.startsWith("blob:")) URL.revokeObjectURL(im.branded_url);
    });
    // eslint-disable-next-line
  }, []);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await adminApi.get("/admin/content-studio/custom-image/history?limit=30");
      const items = (r.data?.items || []).map((im) => ({
        ...im,
        url: `data:${im.mime_type};base64,${im.data_base64}`,
        branded_url: null,
        branded_blob: null,
        branding: false,
      }));
      setHistory(items);
    } catch {
      // history is best-effort — if endpoint is missing (older deploy) we just show none
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, []);

  const generate = async () => {
    setGenerating(true); setErr(null);
    try {
      await adminApi.post("/admin/content-studio/custom-image", {
        prompt, count, aspect_ratio: aspect, style,
      });
      // Refresh history — successfully-generated images are persisted server-side,
      // so even if the response was partially dropped they'll show up here.
      await loadHistory();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setErr(
          "This feature isn't available in your current deployment yet. Redeploy production from preview to enable Custom AI image generation."
        );
      } else {
        setErr(e?.response?.data?.detail || "Image generation failed.");
      }
      // Even on error, refresh history — some images may have been generated and persisted
      // before the request was cut off (e.g. timeout mid-way through a multi-image batch).
      try { await loadHistory(); } catch { /* ignore */ }
    } finally {
      setGenerating(false);
    }
  };

  const downloadImage = (im, branded = false) => {
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
    if (branded && im.branded_blob) {
      downloadBlob(im.branded_blob, `kerala-speaks-custom-${im.id}-branded-${stamp}.png`);
      return;
    }
    const byteString = atob(im.data_base64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    const blob = new Blob([ab], { type: im.mime_type || "image/png" });
    downloadBlob(blob, `kerala-speaks-custom-${im.id}-${stamp}.png`);
  };

  const brandImage = async (im) => {
    setHistory((prev) => prev.map((x) => x.id === im.id ? { ...x, branding: true } : x));
    try {
      const r = await adminApi.post(
        "/admin/content-studio/custom-image/brand",
        { data_base64: im.data_base64 },
        { responseType: "blob" }
      );
      const blob = r.data;
      const url = URL.createObjectURL(blob);
      setHistory((prev) => prev.map((x) => x.id === im.id
        ? { ...x, branded_url: url, branded_blob: blob, branding: false }
        : x
      ));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setErr(
          "Branding endpoint not deployed yet. Redeploy production from preview to enable this."
        );
      } else {
        setErr(e?.response?.data?.detail || "Branding failed.");
      }
      setHistory((prev) => prev.map((x) => x.id === im.id ? { ...x, branding: false } : x));
    }
  };

  const deleteImage = async (im) => {
    try {
      await adminApi.delete(`/admin/content-studio/custom-image/${im.id}`);
      setHistory((prev) => prev.filter((x) => x.id !== im.id));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const generateCaption = async (im) => {
    setCaptionState((prev) => ({ ...prev, [im.id]: { loading: true, captions: null, err: null } }));
    try {
      const body = { kind: "daily", format: "all", hashtags: true };
      if (selectedPoll) {
        body.tone = selectedPoll.suggested_tone || "curious";
        body.extra_notes = `This post is for the campaign: ${selectedPoll.suggested_headline || selectedPoll.title}. ${selectedPoll.suggested_sub || selectedPoll.headline || ""}`.trim();
      }
      const r = await adminApi.post("/admin/content-studio/caption", body);
      setCaptionState((prev) => ({ ...prev, [im.id]: { loading: false, captions: r.data?.captions || {}, err: null } }));
    } catch (e) {
      setCaptionState((prev) => ({ ...prev, [im.id]: {
        loading: false, captions: null,
        err: e?.response?.data?.detail || "Caption generation failed.",
      } }));
    }
  };

  const aspectBoxOf = (ar) => ASPECTS.find((a) => a.v === ar)?.box || "aspect-square";

  return (
    <div className="space-y-5">
      <CampaignPickerCard
        polls={polls} loading={pollsLoading}
        value={pollId} onSelect={setPollId}
        testIdBase="cs-custom"
      />
      <Card title="Describe what you want">
        <Field label="Prompt">
          <Textarea
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={1200}
            placeholder="e.g. A retro Indian newspaper poster celebrating the first 100 days of Kerala's new CM, saffron and green palette, dramatic lighting"
            data-testid="cs-custom-prompt"
          />
        </Field>
        <p className="text-[10px] uppercase font-bold tracking-[0.25em] text-neutral-500 mt-1">
          {prompt.length} / 1200 chars · No text or logos generated on the image — keep the prompt about the visual idea only.
        </p>

        <div className="grid md:grid-cols-3 gap-3 mt-4">
          <Field label="How many images?">
            <Select value={String(count)} onChange={(e) => setCount(Number(e.target.value))} data-testid="cs-custom-count">
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} image{n > 1 ? "s" : ""} · ~₹{(n * 0.039 * 83).toFixed(1)}</option>)}
            </Select>
          </Field>
          <Field label="Aspect ratio">
            <Select value={aspect} onChange={(e) => setAspect(e.target.value)} data-testid="cs-custom-aspect">
              {ASPECTS.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
            </Select>
          </Field>
          <Field label="Style preset (optional)">
            <Select value={style} onChange={(e) => setStyle(e.target.value)} data-testid="cs-custom-style">
              {STYLES.map((s) => <option key={s.v || "none"} value={s.v}>{s.label}</option>)}
            </Select>
          </Field>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Btn onClick={generate} disabled={generating || prompt.trim().length < 5} data-testid="cs-custom-generate">
            <Wand2 size={14} strokeWidth={3} /> {generating ? "Generating…" : `Generate ${count} image${count > 1 ? "s" : ""}`}
          </Btn>
          <Btn variant="ghost" onClick={loadHistory} disabled={historyLoading || generating} data-testid="cs-custom-refresh">
            <RefreshCw size={12} strokeWidth={3} /> Refresh history
          </Btn>
          <p className="text-[10px] uppercase font-bold tracking-[0.25em] text-neutral-500">
            ~10–20s per image · Each successful image is saved here even if your network drops mid-generation.
          </p>
        </div>

        {err && <div className="mt-3"><ErrorBanner message={err} /></div>}
      </Card>

      {generating && (
        <Card title="Working…">
          <Spinner label={`Nano Banana is generating ${count} image${count > 1 ? "s" : ""}. Takes 10–25s per image. They'll appear below as soon as they're ready.`} />
        </Card>
      )}

      <Card
        title={`Your generated images${history.length ? ` (${history.length})` : ""}`}
        action={
          <p className="text-[10px] uppercase font-bold tracking-[0.25em] text-neutral-500">
            Most recent first
          </p>
        }
      >
        {historyLoading && !history.length && <Spinner label="Loading your past generations…" />}
        {!historyLoading && !history.length && (
          <EmptyState
            title="No generated images yet"
            sub="Use the prompt above to create your first one. All successful generations are saved here automatically."
          />
        )}
        {history.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cs-custom-history">
            {history.map((im) => {
              const showBranded = !!im.branded_url;
              const cap = captionState[im.id];
              const box = aspectBoxOf(im.aspect_ratio);
              return (
                <div key={im.id} className="border-2 border-[#1f1f1f] p-3 space-y-3" data-testid={`cs-custom-card-${im.id}`}>
                  <div className={`w-full ${box} border-2 border-[#1f1f1f] bg-[#0A0A0A] overflow-hidden flex items-center justify-center`}>
                    <img
                      src={showBranded ? im.branded_url : im.url}
                      alt="Custom AI generation"
                      data-testid={`cs-custom-img-${im.id}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </div>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <Tag color="#FF9933">{im.aspect_ratio}</Tag>
                    {im.style && <Tag color="#FDFBF7">{im.style}</Tag>}
                    {im.created_at && (
                      <span className="text-[10px] uppercase font-bold tracking-[0.25em] text-neutral-500">
                        {new Date(im.created_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {im.prompt && (
                    <p className="text-xs text-neutral-400 line-clamp-2" title={im.prompt}>
                      {im.prompt}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Btn variant="ghost" onClick={() => downloadImage(im, showBranded)} data-testid={`cs-custom-dl-${im.id}`}>
                      <Download size={12} strokeWidth={3} /> {showBranded ? "Download branded" : "Download PNG"}
                    </Btn>
                    {!showBranded ? (
                      <Btn variant="ghost" onClick={() => brandImage(im)} disabled={im.branding} data-testid={`cs-custom-brand-${im.id}`}>
                        <Stamp size={12} strokeWidth={3} /> {im.branding ? "Branding…" : "Brand it"}
                      </Btn>
                    ) : (
                      <Btn
                        variant="ghost"
                        onClick={() => setHistory((prev) => prev.map((x) => x.id === im.id
                          ? { ...x, branded_url: null, branded_blob: null }
                          : x))}
                        data-testid={`cs-custom-unbrand-${im.id}`}
                      >
                        <RefreshCw size={12} strokeWidth={3} /> Original
                      </Btn>
                    )}
                    <Btn variant="ghost" onClick={() => generateCaption(im)} disabled={cap?.loading} data-testid={`cs-custom-cap-${im.id}`}>
                      <Sparkles size={12} strokeWidth={3} /> {cap?.loading ? "…" : "Caption"}
                    </Btn>
                    <Btn variant="ghost" onClick={() => deleteImage(im)} data-testid={`cs-custom-del-${im.id}`}>
                      Delete
                    </Btn>
                  </div>
                  {cap?.err && <ErrorBanner message={cap.err} />}
                  {cap?.captions && (
                    <div className="space-y-2 pt-1" data-testid={`cs-custom-captions-${im.id}`}>
                      {cap.captions.INSTAGRAM_EN && <CaptionBlock label="Instagram · English" text={cap.captions.INSTAGRAM_EN} testIdBase={`cs-custom-cap-ig-en-${im.id}`} />}
                      {cap.captions.INSTAGRAM_ML && <CaptionBlock label="Instagram · Malayalam" text={cap.captions.INSTAGRAM_ML} testIdBase={`cs-custom-cap-ig-ml-${im.id}`} />}
                      {cap.captions.TWITTER_EN && <CaptionBlock label="X · English" text={cap.captions.TWITTER_EN} testIdBase={`cs-custom-cap-x-en-${im.id}`} />}
                      {cap.captions.WHATSAPP_EN && <CaptionBlock label="WhatsApp · English" text={cap.captions.WHATSAPP_EN} testIdBase={`cs-custom-cap-wa-en-${im.id}`} />}
                      {cap.captions.HASHTAGS && <CaptionBlock label="Hashtags" text={cap.captions.HASHTAGS} testIdBase={`cs-custom-cap-tags-${im.id}`} />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────────
// DRAFTS tab — preserves the existing Claude text-only drafts experience
// ────────────────────────────────────────────────────────────────────────────────

const FORMATS = [
  { v: "instagram_caption", label: "Instagram caption" },
  { v: "x_post", label: "X / Twitter post" },
  { v: "whatsapp", label: "WhatsApp share text" },
  { v: "poster_headline", label: "Poster headline" },
  { v: "top_messages", label: "Top messages summary" },
  { v: "weekly_summary", label: "Weekly summary" },
  { v: "category_summary", label: "Category summary" },
];
const TONES = [
  { v: "civic", label: "Civic" },
  { v: "punchy", label: "Punchy" },
  { v: "warm", label: "Warm" },
  { v: "data-driven", label: "Data-driven" },
];
const CATS = [
  "", "jobs", "waste-management", "roads", "ai-technology", "education",
  "corruption", "youth-opportunities", "healthcare", "cost-of-living",
  "environment", "public-transport", "other",
];

const DraftsTab = () => {
  const [format, setFormat] = useState("x_post");
  const [tone, setTone] = useState("civic");
  const [lang, setLang] = useState("en");
  const [category, setCategory] = useState("");
  const [context, setContext] = useState("");
  const [out, setOut] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const generate = async () => {
    setLoading(true); setErr(null); setOut("");
    try {
      const payload = { format, tone, lang };
      if (category) payload.category = category;
      if (context) payload.context = context;
      const r = await adminApi.post("/admin/content-studio", payload);
      setOut(r.data.content || "");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Generation failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <Card title="Brief">
        <div className="grid md:grid-cols-4 gap-3">
          <Field label="Format">
            <Select value={format} onChange={(e) => setFormat(e.target.value)} data-testid="cs-format">
              {FORMATS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
            </Select>
          </Field>
          <Field label="Tone">
            <Select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="cs-tone">
              {TONES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </Select>
          </Field>
          <Field label="Language">
            <Select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="cs-lang">
              <option value="en">English</option>
              <option value="ml">Malayalam</option>
            </Select>
          </Field>
          <Field label="Category (optional)">
            <Select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="cs-category">
              {CATS.map((c) => <option key={c || "all"} value={c}>{c || "All categories"}</option>)}
            </Select>
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Extra context (optional)">
            <Textarea rows={3} value={context} onChange={(e) => setContext(e.target.value)}
              placeholder="e.g. focus on the protest in Kochi, or mention 100-day milestone"
              data-testid="cs-context" />
          </Field>
        </div>
        <div className="mt-5">
          <Btn onClick={generate} disabled={loading} data-testid="cs-generate">
            <MessageSquareQuote size={14} strokeWidth={3} /> {loading ? "Drafting…" : "Generate draft"}
          </Btn>
        </div>
      </Card>

      {err && <ErrorBanner message={err} />}

      <Card title="Output" action={out && <CopyButton text={out} testId="cs-copy" />}>
        {loading && <Spinner label="Claude is drafting…" />}
        {!loading && !out && !err && (
          <p className="text-sm text-neutral-500">No draft yet. Choose a format and click Generate.</p>
        )}
        {!loading && out && (
          <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans" style={{ color: "#FDFBF7" }} data-testid="cs-output">
            {out}
          </pre>
        )}
      </Card>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase font-extrabold tracking-[0.3em] text-neutral-400 mb-2">{label}</span>
    {children}
  </label>
);
